import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import getConnectedAccounts from '@/settings/fetch/get-connected-accounts'
import type { ConnectedAccount } from '@/settings/schemas/external-storage-schema'
import { pickVisibleAccount } from '@/settings/utils/pick-visible-account'
import type { RootState } from '@/store/store'
import { getMatterExternalSyncMapping } from '@/user_files/external-storage/fetch/get-matter-external-sync-mapping'
import type { MatterExternalSyncMappingPublicRead } from '@/user_files/external-storage/schemas'

export const kExternalStorageRequestStatus = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
} as const

type RequestStatus = (typeof kExternalStorageRequestStatus)[keyof typeof kExternalStorageRequestStatus]

type MatterSyncRequestState = {
  status: RequestStatus
  error: string | null
  requestId?: string
}

export interface ExternalStorageState {
  accounts: ConnectedAccount[]
  accountsRequest: MatterSyncRequestState
  mappingsByMatterId: Record<string, MatterExternalSyncMappingPublicRead | null>
  mappingRequestsByMatterId: Record<string, MatterSyncRequestState>
}

const createIdleRequestState = (): MatterSyncRequestState => ({
  status: kExternalStorageRequestStatus.IDLE,
  error: null,
})
const idleRequestState = createIdleRequestState()

const initialState: ExternalStorageState = {
  accounts: [],
  accountsRequest: createIdleRequestState(),
  mappingsByMatterId: {},
  mappingRequestsByMatterId: {},
}

export const fetchConnectedAccounts = createAsyncThunk('externalStorage/fetchConnectedAccounts', async () => {
  const response = await getConnectedAccounts()
  return response.accounts
})

export const fetchMatterExternalSyncMapping = createAsyncThunk('externalStorage/fetchMatterExternalSyncMapping', async (matterId: string) => {
  const response = await getMatterExternalSyncMapping(matterId)
  return { matterId, mapping: response.mapping }
})

export const externalStorageSlice = createSlice({
  name: 'externalStorageState',
  initialState,
  reducers: {
    accountDisconnected: (state, action: PayloadAction<{ account: ConnectedAccount }>) => {
      const disconnectedAccount = action.payload.account
      state.accounts = state.accounts.map((account) => (account.id === disconnectedAccount.id ? disconnectedAccount : account))

      for (const [matterId, mapping] of Object.entries(state.mappingsByMatterId)) {
        if (mapping?.account_id === disconnectedAccount.id) {
          delete state.mappingsByMatterId[matterId]
          state.mappingRequestsByMatterId[matterId] = createIdleRequestState()
        }
      }
    },
    matterSyncMappingDisconnected: (state, action: PayloadAction<{ matterId: string }>) => {
      state.mappingsByMatterId[action.payload.matterId] = null
      state.mappingRequestsByMatterId[action.payload.matterId] = {
        status: kExternalStorageRequestStatus.SUCCESS,
        error: null,
      }
    },
    nullifyData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectedAccounts.pending, (state, action) => {
        state.accountsRequest = { status: kExternalStorageRequestStatus.LOADING, error: null, requestId: action.meta.requestId }
      })
      .addCase(fetchConnectedAccounts.fulfilled, (state, action) => {
        if (state.accountsRequest.requestId !== action.meta.requestId) return
        state.accounts = action.payload
        state.accountsRequest = { status: kExternalStorageRequestStatus.SUCCESS, error: null }
      })
      .addCase(fetchConnectedAccounts.rejected, (state, action) => {
        if (state.accountsRequest.requestId !== action.meta.requestId) return
        state.accountsRequest = { status: kExternalStorageRequestStatus.ERROR, error: action.error.message ?? 'Failed to load connected accounts' }
      })
      .addCase(fetchMatterExternalSyncMapping.pending, (state, action) => {
        state.mappingRequestsByMatterId[action.meta.arg] = {
          status: kExternalStorageRequestStatus.LOADING,
          error: null,
          requestId: action.meta.requestId,
        }
      })
      .addCase(fetchMatterExternalSyncMapping.fulfilled, (state, action) => {
        if (state.mappingRequestsByMatterId[action.payload.matterId]?.requestId !== action.meta.requestId) return
        state.mappingsByMatterId[action.payload.matterId] = action.payload.mapping
        state.mappingRequestsByMatterId[action.payload.matterId] = { status: kExternalStorageRequestStatus.SUCCESS, error: null }
      })
      .addCase(fetchMatterExternalSyncMapping.rejected, (state, action) => {
        if (state.mappingRequestsByMatterId[action.meta.arg]?.requestId !== action.meta.requestId) return
        state.mappingRequestsByMatterId[action.meta.arg] = {
          status: kExternalStorageRequestStatus.ERROR,
          error: action.error.message ?? 'Failed to load matter sync mapping',
        }
      })
  },
})

const selectExternalStorageState = (state: RootState): ExternalStorageState => state.externalStorageState ?? initialState

export const selectConnectedAccounts = (state: RootState) => selectExternalStorageState(state).accounts

export const selectExternalStorageAccountsRequest = (state: RootState) => selectExternalStorageState(state).accountsRequest

export const selectVisibleConnectedExternalAccount = (state: RootState) => {
  const visibleAccount = pickVisibleAccount(selectConnectedAccounts(state))
  return visibleAccount?.status === 'connected' ? visibleAccount : null
}

export const selectMatterExternalSyncMapping = (state: RootState, matterId: string | null | undefined) => {
  if (!matterId) return null
  return selectExternalStorageState(state).mappingsByMatterId[matterId] ?? null
}

export const selectMatterExternalSyncMappingRequest = (state: RootState, matterId: string | null | undefined) => {
  if (!matterId) return idleRequestState
  return selectExternalStorageState(state).mappingRequestsByMatterId[matterId] ?? idleRequestState
}

export const selectActiveMatterExternalSyncMapping = (state: RootState, matterId: string | null | undefined) => {
  const mapping = selectMatterExternalSyncMapping(state, matterId)
  return mapping?.status === 'active' ? mapping : null
}

export const ExternalStorageActions = {
  ...externalStorageSlice.actions,
  fetchConnectedAccounts,
  fetchMatterExternalSyncMapping,
}

export default externalStorageSlice.reducer
